/*
 * Home · Best Seller (sections/home-bestseller.liquid).
 *
 * - Gallery: a scroll-snap track with thumbnails and prev/next arrows. Like
 *   the Replo carousel it doesn't loop, so the arrow at either end hides.
 * - Add to cart: posts the form to /cart/add.js, then opens the cart drawer
 *   the way assets/bundle-lp.js does (Essential Cart app, then the theme's
 *   #cart-modal, then the cart page). If fetch fails the form posts normally.
 * - Variant picker (only rendered for multi-variant products): keeps the
 *   price, compare-at price, monthly line and button state in sync.
 * - Bread Pay: the live page's loader, ported as-is. Bread's cart.js renders
 *   its checkout button into #bread-checkout-btn-product (invisible, over the
 *   financing box) so a tap opens Bread's modal. cart.js looks the product up
 *   from the page URL, which on the homepage is /products/.js, so that one XHR
 *   is pointed at the real product handle.
 */
(() => {
  if (window.hmBestseller) return;
  window.hmBestseller = { version: 1 };

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Gallery ------------------------------------------------------------- */

  function initGallery(root) {
    const carousel = root.querySelector('[data-hb-carousel]');
    if (!carousel || carousel.dataset.hbReady) return;
    carousel.dataset.hbReady = 'true';

    const viewport = carousel.querySelector('[data-hb-viewport]');
    if (!viewport) return;
    const slides = Array.from(viewport.children);
    const thumbsBox = carousel.querySelector('[data-hb-thumbs]');
    const thumbs = Array.from(carousel.querySelectorAll('[data-hb-thumb]'));
    const prev = carousel.querySelector('[data-hb-prev]');
    const next = carousel.querySelector('[data-hb-next]');
    const status = carousel.querySelector('[data-hb-status]');
    const last = slides.length - 1;
    let current = -1;
    let frame = 0; // pending scroll update (timeout id)

    const offset = (index) => slides[index].offsetLeft - slides[0].offsetLeft;

    function nearestIndex() {
      const x = viewport.scrollLeft;
      let best = 0;
      let distance = Infinity;
      slides.forEach((slide, index) => {
        const d = Math.abs(offset(index) - x);
        if (d < distance) {
          distance = d;
          best = index;
        }
      });
      return best;
    }

    // Keep the active thumbnail in view inside its own strip (never scrolls the page).
    function revealThumb(thumb) {
      if (!thumbsBox || !thumb) return;
      if (thumbsBox.scrollHeight > thumbsBox.clientHeight + 1) {
        const top = thumb.offsetTop;
        const bottom = top + thumb.offsetHeight;
        if (top < thumbsBox.scrollTop) thumbsBox.scrollTop = top;
        else if (bottom > thumbsBox.scrollTop + thumbsBox.clientHeight) {
          thumbsBox.scrollTop = bottom - thumbsBox.clientHeight;
        }
      }
      if (thumbsBox.scrollWidth > thumbsBox.clientWidth + 1) {
        const left = thumb.offsetLeft;
        const right = left + thumb.offsetWidth;
        if (left < thumbsBox.scrollLeft) thumbsBox.scrollLeft = left;
        else if (right > thumbsBox.scrollLeft + thumbsBox.clientWidth) {
          thumbsBox.scrollLeft = right - thumbsBox.clientWidth;
        }
      }
    }

    function update() {
      frame = 0;
      const index = nearestIndex();
      if (prev) prev.disabled = index <= 0;
      if (next) next.disabled = index >= last;
      if (index === current) return;
      current = index;
      thumbs.forEach((thumb, i) => {
        if (i === index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      if (status) status.textContent = `Slide ${index + 1} of ${slides.length}`;
      revealThumb(thumbs[index]);
    }

    function goTo(index) {
      const target = Math.min(Math.max(index, 0), last);
      viewport.scrollTo({ left: offset(target), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }

    viewport.addEventListener(
      'scroll',
      () => {
        if (!frame) frame = setTimeout(update, 50);
      },
      { passive: true }
    );
    if (prev) prev.addEventListener('click', () => goTo(nearestIndex() - 1));
    if (next) next.addEventListener('click', () => goTo(nearestIndex() + 1));
    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => goTo(Number(thumb.dataset.hbThumb)));
    });
    viewport.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        goTo(nearestIndex() + (event.key === 'ArrowRight' ? 1 : -1));
      }
    });

    // Re-align to the current slide when the layout changes (breakpoints).
    let resizeTimer = 0;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (current >= 0) viewport.scrollTo({ left: offset(current), behavior: 'auto' });
        update();
      }, 150);
    });

    update();
  }

  /* Cart ------------------------------------------------------------------ */

  // Same order as assets/bundle-lp.js: the Essential Cart app's drawer when
  // it's installed, else the theme's own cart drawer, else the cart page.
  async function openCartDrawer(cartUrl) {
    const essential = window.essentialCartApi;
    if (essential && typeof essential.openCart === 'function') {
      if (typeof essential.refreshCart === 'function') await essential.refreshCart();
      essential.openCart();
      return;
    }
    const themeDrawer = document.getElementById('cart-modal');
    const trigger = document.querySelector('[target="#cart-modal"], [data-target="#cart-modal"]');
    if (themeDrawer && trigger) {
      if (typeof themeDrawer.reloadContent === 'function') await themeDrawer.reloadContent();
      trigger.click();
      return;
    }
    window.location.assign(cartUrl || '/cart');
  }

  function initBuy(root) {
    const form = root.querySelector('[data-hb-form]');
    if (!form || form.dataset.hbReady) return;
    form.dataset.hbReady = 'true';

    const button = form.querySelector('[data-hb-add]');
    const label = form.querySelector('[data-hb-add-label]');
    const errorBox = form.querySelector('[data-hb-error]');
    const idInput = form.querySelector('[data-hb-variant-id]');
    const select = form.querySelector('[data-hb-variant-select]');
    let busy = false;

    function showError(message) {
      if (!errorBox) return;
      errorBox.textContent = message || '';
      errorBox.hidden = !message;
    }

    form.addEventListener('submit', async (event) => {
      if (!window.fetch || !window.FormData) return; // plain /cart/add post
      event.preventDefault();
      if (busy || (button && button.disabled)) return;
      busy = true;
      if (button) button.setAttribute('aria-busy', 'true');
      showError('');

      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      let response;
      try {
        response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(form),
        });
      } catch (error) {
        // Network/JS trouble: fall back to the normal form post.
        form.submit();
        return;
      }

      try {
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.description || data.message || 'Could not add to cart.');
        }
        await openCartDrawer(form.dataset.cartUrl);
      } catch (error) {
        showError(error.message);
      } finally {
        busy = false;
        if (button) button.removeAttribute('aria-busy');
      }
    });

    if (select) {
      select.addEventListener('change', () => {
        const option = select.selectedOptions[0];
        if (!option) return;
        if (idInput) idInput.value = option.value;

        const price = root.querySelector('[data-hb-price]');
        if (price) price.textContent = option.dataset.price;
        const compare = root.querySelector('[data-hb-compare]');
        if (compare) {
          compare.textContent = option.dataset.compare || '';
          compare.hidden = !option.dataset.compare;
        }
        const savings = root.querySelector('[data-hb-savings]');
        if (savings) savings.hidden = !option.dataset.compare;

        const available = option.dataset.available === 'true';
        if (button) button.disabled = !available;
        if (label && button) {
          label.textContent = available ? button.dataset.label : button.dataset.soldOutLabel;
        }
        updateMonthly(root, Number(option.dataset.priceCents), Number(option.dataset.compareCents));
        showError('');
      });
    }
  }

  function updateMonthly(root, priceCents, compareCents) {
    const text = root.querySelector('[data-hb-bread-text]');
    if (!text || !priceCents) return;
    const months = Number(text.dataset.months) || 36;
    const template = text.dataset.template || 'As low as [monthly]/mo';
    const monthly = (cents) => `$${Math.ceil(cents / 100 / months)}`;
    const line = text.querySelector('.hm-bestseller__bread-line');
    if (!line) return;
    line.textContent = template.replace('[monthly]', monthly(priceCents));
    if (compareCents && compareCents > priceCents) {
      const was = document.createElement('span');
      was.className = 'hm-bestseller__bread-was';
      // Only the old amount, e.g. "$320/mo", not the whole "As low as" line.
      const suffix = template.includes('[monthly]') ? template.split('[monthly]').slice(1).join('[monthly]') : '';
      was.textContent = monthly(compareCents) + suffix;
      line.append(' ', was);
    }
  }

  /* Bread Pay ------------------------------------------------------------- */

  const bread = { handle: null, injected: false, fallbackTimer: 0 };
  const SDK_ID = 'bread-cart-sdk-script'; // reserved by cart.js, never used on our loader
  const ANCHOR = '[data-testid="bread-placement-bread-checkout-btn-product"]';

  const hasAnchor = () => Boolean(document.querySelector(ANCHOR));

  // Point cart.js's one product lookup (/products/<last path segment>.js,
  // which is /products/.js on the homepage) at the real product.
  function installXhrRewrite() {
    const Native = window.XMLHttpRequest;
    if (!Native || Native.hbBreadPatched) return;
    const segment = window.location.pathname.replace(/\/$/, '').split('/').pop();
    const bogusA = `/products/${segment}.js`;
    const bogusB = '/products/.js';
    let done = false;

    function Patched() {
      const instance = new Native();
      const open = instance.open.bind(instance);
      instance.open = function patchedOpen(method, url, ...rest) {
        let target = url;
        try {
          if (!done && bread.handle && typeof url === 'string') {
            const path = url.split('?')[0];
            if (path === bogusA || path === bogusB) {
              target = `/products/${bread.handle}.js`;
              done = true;
            }
          }
        } catch (error) {
          /* keep the original URL */
        }
        return open(method, target, ...rest);
      };
      return instance;
    }
    Patched.hbBreadPatched = true;
    Object.keys(Native).forEach((key) => {
      try {
        Patched[key] = Native[key];
      } catch (error) {
        /* read-only static */
      }
    });
    window.XMLHttpRequest = Patched;
    setTimeout(() => {
      if (window.XMLHttpRequest === Patched) window.XMLHttpRequest = Native;
    }, 20000);
  }

  function injectCartJs(shopId) {
    if (bread.injected) return;
    bread.injected = true;
    if (window.BreadPayments || document.getElementById(SDK_ID)) return;
    const script = document.createElement('script');
    script.src = `https://callback.redirect.pp-prod-ads.breadgateway.net/api/shopify-plugin-backend/messaging-app/cart.js?shopId=${encodeURIComponent(shopId)}&_t=${Date.now()}`;
    document.documentElement.appendChild(script);
  }

  // Campaign pricing (Theme settings > Campaign pricing): Bread's pop-up
  // reads the price from /products/<handle>.js. While a campaign price applies
  // (data-campaign-price on the section, in cents) that response gets the
  // campaign price, so Bread finances the Discounted Price. Any parse problem
  // returns the response untouched.
  function installCampaignPrice(handle, priceCents) {
    const Native = window.XMLHttpRequest;
    if (!Native || !handle || !(priceCents > 0) || Native.campaignPricePatched) return;
    const proto = Native.prototype;
    const textDesc = Object.getOwnPropertyDescriptor(proto, 'responseText');
    const respDesc = Object.getOwnPropertyDescriptor(proto, 'response');
    if (!textDesc || !textDesc.get) return;
    const target = `/products/${handle}.js`;
    const nativeOpen = proto.open;
    proto.open = function campaignOpen(method, url, ...rest) {
      try {
        this.campaignPrice = typeof url === 'string' && url.split('?')[0].endsWith(target);
      } catch (error) {
        this.campaignPrice = false;
      }
      return nativeOpen.call(this, method, url, ...rest);
    };
    const rewrite = (raw) => {
      try {
        const data = JSON.parse(raw);
        const lower = (value) => (typeof value === 'number' && value > priceCents ? priceCents : value);
        if (typeof data.price === 'number' && data.price > priceCents) {
          data.compare_at_price = Math.max(data.compare_at_price || 0, data.price);
        }
        data.price = lower(data.price);
        data.price_min = lower(data.price_min);
        data.price_max = lower(data.price_max);
        (data.variants || []).forEach((variant) => {
          if (typeof variant.price === 'number' && variant.price > priceCents) {
            variant.compare_at_price = Math.max(variant.compare_at_price || 0, variant.price);
            variant.price = priceCents;
          }
        });
        return JSON.stringify(data);
      } catch (error) {
        return raw;
      }
    };
    Object.defineProperty(proto, 'responseText', {
      configurable: true,
      get() {
        const raw = textDesc.get.call(this);
        return this.campaignPrice && this.readyState === 4 ? rewrite(raw) : raw;
      },
    });
    if (respDesc && respDesc.get) {
      Object.defineProperty(proto, 'response', {
        configurable: true,
        get() {
          const value = respDesc.get.call(this);
          return this.campaignPrice && this.readyState === 4 && typeof value === 'string' ? rewrite(value) : value;
        },
      });
    }
    Native.campaignPricePatched = true;
  }

  function initBread(root) {
    const widget = root.querySelector('[data-hb-bread]');
    if (!widget || widget.dataset.hbReady) return;
    widget.dataset.hbReady = 'true';
    const shopId = widget.dataset.shopId;
    if (!shopId) return;
    bread.handle = root.dataset.productHandle || bread.handle;
    installCampaignPrice(bread.handle, Number(root.dataset.campaignPrice));

    installXhrRewrite();
    injectCartJs(shopId);

    // One-time fallback: no Bread anchor after 6s (e.g. the section was
    // re-rendered in the theme editor): remove Bread's scripts and bootstrap again.
    clearTimeout(bread.fallbackTimer);
    bread.fallbackTimer = setTimeout(() => {
      if (hasAnchor() || window.location.pathname.indexOf('/products/') !== -1) return;
      document
        .querySelectorAll(`script[src*="cart.js"], script[src*="connect.breadpayments.com/sdk"], #${SDK_ID}`)
        .forEach((script) => script.remove());
      try {
        delete window.BreadPayments;
      } catch (error) {
        window.BreadPayments = undefined;
      }
      bread.injected = false;
      installXhrRewrite();
      injectCartJs(shopId);
    }, 6000);

    // Tapped before Bread rendered its anchor: kick off the injection again.
    const overlay = widget.querySelector('#bread-checkout-btn-product');
    if (overlay) {
      overlay.addEventListener('click', () => {
        if (!hasAnchor()) {
          bread.injected = false;
          installXhrRewrite();
          injectCartJs(shopId);
        }
      });
    }
  }

  /* Boot ------------------------------------------------------------------ */

  function init(root) {
    if (!root) return;
    initGallery(root);
    initBuy(root);
    initBread(root);
  }

  function initAll() {
    document.querySelectorAll('[data-hb-root]').forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();

  document.addEventListener('shopify:section:load', (event) => {
    init(event.target.querySelector('[data-hb-root]'));
  });
})();
